import { computed, inject, Injectable, resource } from '@angular/core';

import { SessionStore } from 'app/core/services/session.store';

import { NotificationGateway } from '../gateway/notification-gateway';
import { NotificationDto, NotificationId } from '../models/notification';

@Injectable({ providedIn: 'root' })
export class NotificationResource {
    private readonly gateway = inject(NotificationGateway);
    private readonly user = inject(SessionStore).user;

    private readonly inbox = resource({
        params: () => this.user()?.id,
        loader: () => this.gateway.getNotifications(),
    });

    readonly items = computed<readonly NotificationDto[]>(() =>
        this.inbox.hasValue() ? (this.inbox.value() ?? []) : [],
    );
    // First load with nothing cached yet — render the skeleton.
    readonly loading = computed(() => this.inbox.isLoading() && !this.inbox.hasValue());
    readonly error = this.inbox.error;
    readonly settled = computed(() => this.inbox.hasValue() || this.error() !== undefined);

    findById(id: NotificationId): NotificationDto | undefined {
        return this.items().find((notification) => notification.id === id);
    }

    reload(): void {
        this.inbox.reload();
    }

    setRead(expectedUserId: string, id: NotificationId, read: boolean): boolean {
        if (this.user()?.id !== expectedUserId || !this.inbox.hasValue()) return false;

        const notification = this.findById(id);
        if (!notification || notification.read === read) return false;

        const notifications = this.inbox.value();
        this.inbox.set(notifications.map((item) => (item.id === id ? { ...item, read } : item)));

        return true;
    }

    setAllRead(expectedUserId: string): boolean {
        if (this.user()?.id !== expectedUserId || !this.inbox.hasValue()) return false;

        const notifications = this.inbox.value();
        if (!notifications.some((notification) => !notification.read)) return false;

        this.inbox.set(notifications.map((notification) => ({ ...notification, read: true })));
        return true;
    }
}
